import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Table, TableBody, TableCell, TableContainer,
  TableRow, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup, Tooltip, Chip,
} from '@mui/material';
import {
  InsightsOutlined as AnalyticsIcon, OpenInNew as OpenIcon, Refresh as RefreshIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TEAL = '#0d9488';
// Flat panels: small square-ish corners, hairline border, no shadow.
const PANEL = { borderRadius: '4px', border: '1px solid rgba(15, 23, 42, 0.12)', boxShadow: 'none', bgcolor: '#fff' };
const ROW_LINE = '1px solid rgba(15, 23, 42, 0.06)';
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const RANGES = [{ days: 1, label: '24 hours' }, { days: 7, label: '7 days' }, { days: 30, label: '30 days' }, { days: 90, label: '90 days' }];

const num = (n) => Intl.NumberFormat().format(n || 0);
const duration = (seconds) => {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
const regionName = (() => {
  try { const d = new Intl.DisplayNames(undefined, { type: 'region' }); return (c) => d.of(c) || c; } catch { return (c) => c; }
})();

/** Percent change vs the previous period of the same length, or null when there's nothing to compare. */
function change(now, before) {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
}

/** Umami only returns buckets that had visits; fill the gaps so the chart is a timeline. */
function fillTimeline(series, days) {
  const hourly = days === 1;
  const key = (t) => (hourly ? t.slice(0, 13) : t.slice(0, 10)); // "YYYY-MM-DD HH" or "YYYY-MM-DD"
  const byKey = new Map(series.map((p) => [key(p.time), p]));
  const pad = (n) => String(n).padStart(2, '0');
  const out = [];
  const now = new Date();
  const steps = hourly ? 24 : days;
  for (let i = steps - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    if (hourly) d.setHours(now.getHours() - i, 0, 0, 0); else { d.setDate(now.getDate() - i); d.setHours(0, 0, 0, 0); }
    const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}${hourly ? ` ${pad(d.getHours())}` : ''}`;
    const time = hourly ? `${k}:00:00` : `${k} 00:00:00`;
    out.push(byKey.get(k) || { time, visitors: 0, pageviews: 0 });
  }
  return out;
}

function StatTile({ label, value, delta, invert = false, hint }) {
  // For bounce rate a rise is bad, so the colour logic flips; the arrow still shows direction.
  const good = delta == null ? null : (invert ? delta <= 0 : delta >= 0);
  return (
    <Card elevation={0} sx={{ ...PANEL, height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{value}</Typography>
        {delta != null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: good ? '#047857' : '#b45309' }}>
            {delta >= 0 ? <UpIcon sx={{ fontSize: 16 }} /> : <DownIcon sx={{ fontSize: 16 }} />}
            <Typography variant="caption" fontWeight={700}>{Math.abs(delta)}%</Typography>
            <Typography variant="caption" color="text.secondary">vs previous period</Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{hint || ' '}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** Visitors per day (or hour) as thin bars; hover a bar for its numbers. */
function VisitorChart({ series, hourly }) {
  // Drawn at the container's real pixel width, so text stays 11px on a phone and the
  // chart never forces the page wider than the screen.
  const boxRef = useRef(null);
  const [W, setW] = useState(760);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setW(Math.max(240, Math.floor(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 200; const PAD_L = 36; const PAD_B = 22; const PAD_T = 8;
  const max = Math.max(1, ...series.map((p) => p.visitors));
  const step = (W - PAD_L) / Math.max(1, series.length);
  const barW = Math.max(2, Math.min(28, step - 2));
  const y = (v) => PAD_T + (H - PAD_T - PAD_B) * (1 - v / max);
  const ticks = [0, Math.ceil(max / 2), max];
  const labelEvery = Math.ceil(series.length / Math.max(2, Math.floor((W - PAD_L) / 70)));
  const fmt = (t) => {
    const d = new Date(t.replace(' ', 'T'));
    return hourly ? d.toLocaleTimeString(undefined, { hour: 'numeric' }) : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };
  return (
    <Box ref={boxRef} sx={{ width: '100%', minWidth: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Visitors over time" style={{ display: 'block', maxWidth: '100%' }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={W} y1={y(t)} y2={y(t)} stroke="rgba(15,23,42,0.08)" />
            <text x={PAD_L - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#64748b">{t}</text>
          </g>
        ))}
        {series.map((p, i) => {
          const x = PAD_L + i * step + (step - barW) / 2;
          const top = y(p.visitors); const h = H - PAD_B - top;
          return (
            <Tooltip key={p.time} arrow title={`${fmt(p.time)} — ${num(p.visitors)} visitors, ${num(p.pageviews)} page views`}>
              <g style={{ cursor: 'default' }}>
                {/* Hit target taller and wider than the bar. */}
                <rect x={PAD_L + i * step} y={PAD_T} width={step} height={H - PAD_T - PAD_B} fill="transparent" />
                {h > 0 && <path d={`M${x},${H - PAD_B} v${-Math.max(0, h - 2)} q0,-2 2,-2 h${barW - 4} q2,0 2,2 v${Math.max(0, h - 2)} z`} fill={TEAL} />}
                {i % labelEvery === 0 && (
                  <text x={x + barW / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#64748b">{fmt(p.time)}</text>
                )}
              </g>
            </Tooltip>
          );
        })}
        <line x1={PAD_L} x2={W} y1={H - PAD_B} y2={H - PAD_B} stroke="rgba(15,23,42,0.2)" />
      </svg>
    </Box>
  );
}

function TopList({ title, rows, empty, nameOf = (n) => n }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Paper elevation={0} sx={{ ...PANEL, height: '100%', overflow: 'hidden' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>{title}</Typography>
      <TableContainer>
        <Table size="small">
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell sx={{ color: 'text.secondary', borderBottom: 0 }}>{empty}</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.name} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ position: 'relative', borderBottom: ROW_LINE }}>
                  <Box sx={{ position: 'absolute', inset: '4px auto 4px 8px', width: `calc((100% - 16px) * ${r.count / max})`, bgcolor: 'rgba(13,148,136,0.10)', borderRadius: '2px' }} />
                  <Typography variant="body2" sx={{ position: 'relative', wordBreak: 'break-all' }}>{nameOf(r.name)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: 72, borderBottom: ROW_LINE }}>
                  <Typography variant="body2" fontWeight={600}>{num(r.count)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function WebsiteAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/website-analytics`, { params: { days: d, tz: TZ } });
      setData(res.data);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Failed to load website analytics');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const s = data?.summary;
  const bounceRate = s && s.visits ? Math.round((Math.min(s.bounces, s.visits) / s.visits) * 100) : 0;
  const prevBounce = s && s.previous.visits ? Math.round((Math.min(s.previous.bounces, s.previous.visits) / s.previous.visits) * 100) : null;
  const avgVisit = s && s.visits ? s.totaltime / s.visits : 0;
  const prevAvg = s && s.previous.visits ? s.previous.totaltime / s.previous.visits : null;
  const opened = data?.events?.find((e) => e.name === 'book-call-open')?.count || 0;
  const sent = data?.events?.find((e) => e.name === 'book-call-submit')?.count || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <AnalyticsIcon sx={{ color: TEAL }} />
        <Typography variant="h4" fontWeight={800} sx={{ flexGrow: 1 }}>Website analytics</Typography>
        {data?.dashboard_url && (
          <Button size="small" endIcon={<OpenIcon />} href={data.dashboard_url} target="_blank" rel="noopener noreferrer">
            Full dashboard
          </Button>
        )}
      </Box>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Who visits scribelive.us. Counted without cookies or personal data.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)} sx={{ '& .MuiToggleButton-root': { borderRadius: '4px' }, '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }, '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}>
          {RANGES.map((r) => <ToggleButton key={r.days} value={r.days}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load(days)} disabled={loading}>Refresh</Button>
        {data?.configured && (
          <Chip
            size="small"
            sx={{ ml: 'auto', borderRadius: '4px' }}
            icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: data.active_now ? '#10b981' : '#94a3b8', ml: 1 }} />}
            label={`${num(data.active_now)} on the site right now`}
          />
        )}
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress sx={{ color: '#c9a227' }} /></Box>
      ) : error && !data ? (
        <Alert severity="error">{error}</Alert>
      ) : data && !data.configured ? (
        <Alert severity="info">Website analytics is not configured on this server yet.</Alert>
      ) : data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={2.4}><StatTile label="Visitors" value={num(s.visitors)} delta={change(s.visitors, s.previous.visitors)} hint="unique people" /></Grid>
            <Grid item xs={6} md={2.4}><StatTile label="Visits" value={num(s.visits)} delta={change(s.visits, s.previous.visits)} /></Grid>
            <Grid item xs={6} md={2.4}><StatTile label="Page views" value={num(s.pageviews)} delta={change(s.pageviews, s.previous.pageviews)} /></Grid>
            <Grid item xs={6} md={2.4}><StatTile label="Bounce rate" value={`${bounceRate}%`} delta={prevBounce == null ? null : bounceRate - prevBounce} invert hint="left after one page" /></Grid>
            <Grid item xs={12} md={2.4}><StatTile label="Average visit" value={duration(avgVisit)} delta={prevAvg ? change(avgVisit, prevAvg) : null} /></Grid>
          </Grid>

          <Paper elevation={0} sx={{ ...PANEL, p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Visitors per {days === 1 ? 'hour' : 'day'}</Typography>
            {data.series.length === 0
              ? <Typography color="text.secondary" variant="body2">No visits in this period yet.</Typography>
              : <VisitorChart series={fillTimeline(data.series, days)} hourly={days === 1} />}
          </Paper>

          <Paper elevation={0} sx={{ ...PANEL, p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>Book a call</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              How many visitors open the booking form, and how many send it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box><Typography variant="h5" fontWeight={800}>{num(opened)}</Typography><Typography variant="caption" color="text.secondary">opened the form</Typography></Box>
              <Box><Typography variant="h5" fontWeight={800}>{num(sent)}</Typography><Typography variant="caption" color="text.secondary">sent a request</Typography></Box>
              <Box>
                <Typography variant="h5" fontWeight={800}>{s.visitors ? `${((sent / s.visitors) * 100).toFixed(1)}%` : '—'}</Typography>
                <Typography variant="caption" color="text.secondary">of visitors booked a call</Typography>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TopList title="Top pages" rows={data.top_pages} empty="No page views yet." /></Grid>
            <Grid item xs={12} md={6}><TopList title="Where visitors came from" rows={data.referrers} empty="No referrers yet — direct visits aren't listed." /></Grid>
            <Grid item xs={12} md={4}><TopList title="Countries" rows={data.countries} empty="No data yet." nameOf={regionName} /></Grid>
            <Grid item xs={12} md={4}><TopList title="Devices" rows={data.devices} empty="No data yet." /></Grid>
            <Grid item xs={12} md={4}><TopList title="Events" rows={data.events.map((e) => ({ name: e.label, count: e.count }))} empty="No events yet." /></Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
